import { useCallback, useMemo, useRef, useState } from "react";
import {
  DEFAULT_CARD_ASPECT,
  MAX_CARD_ASPECT,
  MIN_CARD_ASPECT,
  isSaneAspectRatio,
  resolveGroupAspect,
} from "util/mediaAspect";

/**
 * Per-item aspect ratio hint. May come from server-stored metadata or from
 * client-side intrinsic measurement at render time.
 */
export interface MediaAspectHint {
  /** Stable id for the item (e.g. media key, file ordinal, eid+idx). */
  id: string;
  /** Known aspect ratio if any (e.g. from `extended_metadata.aspectRatio`). */
  aspectRatio?: number | null;
}

export interface UseMediaAspectOptions {
  /** Aspect used before any item has reported a ratio. Defaults to 4:5. */
  fallback?: number;
  /** Lower clamp for the resolved group aspect. */
  min?: number;
  /** Upper clamp for the resolved group aspect. */
  max?: number;
}

export interface UseMediaAspectResult {
  /** The current resolved aspect ratio for the whole group. */
  aspect: number;
  /** Inline style suitable for `style={{ aspectRatio: ... }}`. */
  aspectStyle: { aspectRatio: string };
  /**
   * Report a measured aspect ratio for an item by id. Safe to call from
   * `<img onLoad>` / `<video onLoadedMetadata>` handlers; updates state only
   * when the resolved group aspect actually changes.
   */
  onMeasure: (id: string, aspectRatio: number) => void;
  /**
   * Live ratios keyed by id. Useful for callers that want to render
   * per-item aspects within a uniform group container.
   */
  ratios: Readonly<Record<string, number>>;
}

/**
 * Tracks intrinsic aspect ratios for a group of media items and exposes a
 * single resolved aspect so the carousel/grid does not jump heights as
 * items load in. The resolved aspect is the median of valid ratios clamped
 * to a configurable band.
 *
 * The hook is UI-agnostic: it does not hold refs to a carousel or DOM
 * elements (rule 16, 18). Components measure on `onLoad` and forward the
 * value via `onMeasure`.
 */
export function useMediaAspect(
  items: ReadonlyArray<MediaAspectHint>,
  options: UseMediaAspectOptions = {},
): UseMediaAspectResult {
  const fallback = options.fallback ?? DEFAULT_CARD_ASPECT;
  const min = options.min ?? MIN_CARD_ASPECT;
  const max = options.max ?? MAX_CARD_ASPECT;

  const [measured, setMeasured] = useState<Record<string, number>>({});
  // Snapshot ref so `onMeasure` can short-circuit equal updates without
  // re-rendering the calling image element.
  const measuredRef = useRef<Record<string, number>>({});
  measuredRef.current = measured;

  const seedRatios = useMemo<ReadonlyArray<number | undefined>>(() => {
    return items.map((item) =>
      isSaneAspectRatio(item.aspectRatio ?? null)
        ? Number(item.aspectRatio)
        : undefined,
    );
  }, [items]);

  const aspect = useMemo<number>(() => {
    const ratios: Array<number | undefined> = items.map((item, index) => {
      const measuredValue = measured[item.id];
      if (isSaneAspectRatio(measuredValue)) {
        return measuredValue;
      }
      return seedRatios[index];
    });
    if (ratios.every((value) => value === undefined)) {
      return resolveGroupAspect([], fallback);
    }
    return resolveGroupAspect(ratios, fallback);
  }, [items, measured, seedRatios, fallback]);

  const clamped = aspect < min ? min : aspect > max ? max : aspect;

  const onMeasure = useCallback((id: string, aspectRatio: number): void => {
    if (!id || !isSaneAspectRatio(aspectRatio)) {
      return;
    }
    const previous = measuredRef.current[id];
    if (previous && Math.abs(previous - aspectRatio) < 0.0005) {
      return;
    }
    setMeasured((prev) => ({ ...prev, [id]: aspectRatio }));
  }, []);

  const aspectStyle = useMemo(
    () => ({ 
      aspectRatio: `${clamped}`,
      maxHeight: "100%",
      maxWidth: "100%",
    }),
    [clamped],
  );

  return {
    aspect: clamped,
    aspectStyle,
    onMeasure,
    ratios: measured,
  };
}
